import { gql } from '@urql/core';

export const GET_COUNTRIES = gql`
  query GetCountries($options: CountryListOptions) {
    countries(options: $options) {
      items {
        id
        code
        name
        enabled
      }
      totalItems
    }
  }
`;

export const GET_TAX_CATEGORIES = gql`
  query GetTaxCategories {
    taxCategories(options: { take: 100 }) {
      items {
        id
        name
        isDefault
      }
      totalItems
    }
  }
`;

export const GET_GLOBAL_SETTINGS = gql`
  query GetGlobalSettings {
    globalSettings {
      id
      availableLanguages
      serverConfig {
        orderProcess {
          name
          to
        }
        permittedAssetTypes
      }
    }
  }
`;

export const GET_ACTIVE_CHANNEL = gql`
  query GetActiveChannel {
    activeChannel {
      id
      code
      token
      defaultLanguageCode
      currencyCode
      pricesIncludeTax
      customFields {
        shippingTaxEnabled
      }
      defaultShippingZone {
        id
        name
      }
      defaultTaxZone {
        id
        name
      }
    }
  }
`;

export const GET_CURRENT_TENANT = gql`
  query GetCurrentTenant {
    currentTenant {
      id
      domain
      channelCode
      channelId
      countryCode
      plan
      status
      features
      metadata
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_TENANT_CONFIG = gql`
  mutation UpdateTenantConfig($input: UpdateTenantConfigInput!) {
    updateTenantConfig(input: $input) {
      id
      metadata
      features
    }
  }
`;

export const GET_TENANT_VAT_MARKETS = gql`
  query GetTenantVatMarkets {
    tenantVatMarkets {
      countryCode
      status
      vatNumber
      vatCertificateAssetId
      exemptionReason
      registrationDate
    }
  }
`;

export const UPDATE_TENANT_VAT_MARKETS = gql`
  mutation UpdateTenantVatMarkets(
    $markets: [VatMarketInput!]!
    $setup: VatSetupPolicyInput
  ) {
    updateTenantVatMarkets(markets: $markets, setup: $setup) {
      countryCode
      status
      vatNumber
      vatCertificateAssetId
      exemptionReason
      registrationDate
    }
  }
`;

// Tenant Signup (Shop API — public, no auth required)

export const VERIFY_MERCHANT_EMAIL = gql`
  mutation VerifyMerchantEmail($token: String!) {
    verifyMerchantEmail(token: $token) {
      success
      message
      redirectUrl
    }
  }
`;

export const RESEND_VERIFICATION = gql`
  mutation ResendVerification($email: String!) {
    resendVerificationEmail(email: $email) {
      success
      message
    }
  }
`;

export const GET_CHANNELS = gql`
  query GetChannels {
    channels {
      items {
        id
        code
        token
        currencyCode
        defaultLanguageCode
      }
    }
  }
`;

export const ASSIGN_PRODUCTS_TO_CHANNEL = gql`
  mutation AssignProductsToChannel($input: AssignProductsToChannelInput!) {
    assignProductsToChannel(input: $input) {
      id
      name
      channels {
        id
        code
      }
    }
  }
`;

export const REMOVE_PRODUCTS_FROM_CHANNEL = gql`
  mutation RemoveProductsFromChannel($input: RemoveProductsFromChannelInput!) {
    removeProductsFromChannel(input: $input) {
      id
      name
      channels {
        id
        code
      }
    }
  }
`;

export const GET_ROLE_TEMPLATES = gql`
  query GetRoleTemplates {
    roleTemplates {
      code
      label
      description
      permissions
    }
  }
`;

export const GET_PERMISSION_GROUPS = gql`
  query GetPermissionGroups {
    permissionGroups {
      key
      label
      permissions {
        permission
        label
      }
    }
  }
`;

export const CREATE_ROLE_FROM_TEMPLATE = gql`
  mutation CreateRoleFromTemplate($templateCode: String!) {
    createRoleFromTemplate(templateCode: $templateCode) {
      id
      code
      description
      permissions
    }
  }
`;

export const GET_ROLES = gql`
  query GetRoles($options: RoleListOptions) {
    roles(options: $options) {
      items {
        id
        code
        description
        permissions
        channels {
          id
          code
        }
      }
      totalItems
    }
  }
`;

export const CREATE_ROLE = gql`
  mutation CreateRole($input: CreateRoleInput!) {
    createRole(input: $input) {
      id
      code
      description
      permissions
    }
  }
`;

export const GET_ADMINISTRATORS = gql`
  query GetAdministrators($options: AdministratorListOptions) {
    administrators(options: $options) {
      items {
        id
        firstName
        lastName
        emailAddress
        createdAt
        user {
          id
          lastLogin
          roles {
            id
            code
            description
            permissions
          }
        }
      }
      totalItems
    }
  }
`;

export const GET_ADMINISTRATOR_DETAIL = gql`
  query GetAdministratorDetail($id: ID!) {
    administrator(id: $id) {
      id
      firstName
      lastName
      emailAddress
      createdAt
      user {
        id
        lastLogin
        roles {
          id
          code
          description
          permissions
          channels {
            id
            code
          }
        }
      }
    }
  }
`;

export const UPDATE_ADMINISTRATOR = gql`
  mutation UpdateAdministrator($input: UpdateAdministratorInput!) {
    updateAdministrator(input: $input) {
      id
      firstName
      lastName
      emailAddress
      user {
        id
        roles {
          id
          code
          description
          permissions
        }
      }
    }
  }
`;

export const DELETE_ADMINISTRATOR = gql`
  mutation DeleteAdministrator($id: ID!) {
    deleteAdministrator(id: $id) {
      result
      message
    }
  }
`;

export const INVITE_STAFF_MEMBER = gql`
  mutation InviteStaffMember($input: InviteStaffInput!) {
    inviteStaffMember(input: $input) {
      success
      invite {
        id
      }
      message
    }
  }
`;

export const GET_STAFF_INVITES = gql`
  query GetStaffInvites($options: StaffInviteListOptions) {
    staffInvites(options: $options) {
      items {
        id
        email
        firstName
        lastName
        roleId
        status
        expiresAt
        createdAt
      }
      totalItems
    }
  }
`;

export const REVOKE_STAFF_INVITE = gql`
  mutation RevokeStaffInvite($id: ID!) {
    revokeStaffInvite(id: $id) {
      success
      message
    }
  }
`;

export const RESEND_STAFF_INVITE = gql`
  mutation ResendStaffInvite($id: ID!) {
    resendStaffInvite(id: $id) {
      success
      message
    }
  }
`;

export const ACCEPT_STAFF_INVITE = gql`
  mutation AcceptStaffInvite($token: String!, $password: String!) {
    acceptStaffInvite(token: $token, password: $password) {
      success
      message
      emailAddress
    }
  }
`;

export const GET_ACTIVE_ADMINISTRATOR = gql`
  query GetActiveAdministrator {
    activeAdministrator {
      id
      firstName
      lastName
      emailAddress
      createdAt
      user {
        id
        lastLogin
        roles {
          id
          code
          description
        }
      }
    }
  }
`;

export const UPDATE_ACTIVE_ADMINISTRATOR = gql`
  mutation UpdateActiveAdministrator($input: UpdateActiveAdministratorInput!) {
    updateActiveAdministrator(input: $input) {
      id
      firstName
      lastName
      emailAddress
    }
  }
`;

export const SEND_STAFF_PASSWORD_RESET = gql`
  mutation SendStaffPasswordReset($administratorId: ID!) {
    sendStaffPasswordReset(administratorId: $administratorId) {
      success
      message
    }
  }
`;

export const RESET_STAFF_PASSWORD = gql`
  mutation ResetStaffPassword($token: String!, $password: String!) {
    resetStaffPassword(token: $token, password: $password) {
      success
      message
      emailAddress
    }
  }
`;

export const DEACTIVATE_STAFF_MEMBER = gql`
  mutation DeactivateStaffMember($administratorId: ID!) {
    deactivateStaffMember(administratorId: $administratorId) {
      success
      message
    }
  }
`;

export const REACTIVATE_STAFF_MEMBER = gql`
  mutation ReactivateStaffMember($administratorId: ID!) {
    reactivateStaffMember(administratorId: $administratorId) {
      success
      message
    }
  }
`;

export const GET_DEACTIVATED_STAFF = gql`
  query GetDeactivatedStaff {
    deactivatedStaff {
      items {
        id
        firstName
        lastName
        emailAddress
        createdAt
        deactivatedAt
      }
      totalItems
    }
  }
`;

export const GET_STAFF_ACTIVITY = gql`
  query GetStaffActivity($userId: ID!, $options: ActivityLogListOptions) {
    staffActivity(userId: $userId, options: $options) {
      items {
        id
        userId
        action
        resourceType
        resourceId
        description
        changes
        metadata
        status
        createdAt
      }
      totalItems
    }
  }
`;

export const GET_ACTIVITY_LOGS = gql`
  query GetActivityLogs($options: ActivityLogListOptions) {
    activityLogs(options: $options) {
      items {
        id
        userId
        action
        resourceType
        resourceId
        description
        changes
        metadata
        status
        createdAt
      }
      totalItems
    }
  }
`;

export const SALES_CHANNEL_FRAGMENT = gql`
  fragment SalesChannelFields on SalesChannel {
    id
    createdAt
    updatedAt
    code
    type
    name
    description
    isActive
    isDefault
    position
    translations {
      id
      languageCode
      name
      description
    }
  }
`;

export const GET_SALES_CHANNELS = gql`
  ${SALES_CHANNEL_FRAGMENT}
  query GetSalesChannels($options: SalesChannelListOptions) {
    salesChannels(options: $options) {
      items {
        ...SalesChannelFields
      }
      totalItems
    }
  }
`;

export const UPDATE_SALES_CHANNEL = gql`
  ${SALES_CHANNEL_FRAGMENT}
  mutation UpdateSalesChannel($input: UpdateSalesChannelInput!) {
    updateSalesChannel(input: $input) {
      ...SalesChannelFields
    }
  }
`;

export const GET_STORE_LAUNCH_READINESS = gql`
  query GetStoreLaunchReadiness {
    storeLaunchReadiness {
      desiredStatus
      effectiveStatus
      ready
      blockers { code }
      provisioningStatus
      provisioningStage
      provisioningAttempt
      canRetryProvisioning
    }
  }
`;

export const UPDATE_STORE_STATUS = gql`
  mutation UpdateStoreStatus($status: StoreDesiredStatus!) {
    updateStoreStatus(status: $status) {
      desiredStatus
      effectiveStatus
      ready
      blockers { code }
      provisioningStatus
      provisioningStage
      provisioningAttempt
      canRetryProvisioning
    }
  }
`;

export const RETRY_CURRENT_TENANT_PROVISIONING = gql`
  mutation RetryCurrentTenantProvisioning {
    retryCurrentTenantProvisioning {
      desiredStatus
      effectiveStatus
      ready
      blockers { code }
      provisioningStatus
      provisioningStage
      provisioningAttempt
      canRetryProvisioning
    }
  }
`;

export const PLATFORM_COUNTRIES = gql`
  query PlatformCountries {
    platformCountries {
      code
      nameEn
      nameAr
      flag
      currency
      currencySymbol
      currencyPrecision
      dialingCode
      vatRate
      mobilePrefixes
      nationalNumberLengthMin
      nationalNumberLengthMax
      phoneDisplayFormat
      phoneExampleNumber
      otpSmsEnabled
    }
  }
`;

export const GET_CHECKOUT_SETTINGS = gql`
  query GetCheckoutSettings {
    activeChannel {
      id
      customFields {
        guestCheckoutEnabled
        minOrderAmount
        maxOrderAmount

        deliveryNotesEnabled
        deliveryNotesLabel
        lowStockThreshold
        giftWrappingEnabled
        giftOrdersEnabled

        orderCancelEnabled
        orderCancelWindowHours
        orderCancelRequireReason

        enableB2bCheckout
      }
    }
  }
`;

export const UPDATE_TAX_SETTINGS = gql`
  mutation UpdateTaxSettings($input: UpdateChannelInput!) {
    updateChannel(input: $input) {
      ... on Channel {
        id
        pricesIncludeTax
      }
      ... on LanguageNotAvailableError {
        errorCode
        message
      }
    }
  }
`;

export const UPDATE_CHECKOUT_SETTINGS = gql`
  mutation UpdateCheckoutSettings($input: UpdateChannelInput!) {
    updateChannel(input: $input) {
      ... on Channel {
        id
        customFields {
          guestCheckoutEnabled
          minOrderAmount
          maxOrderAmount

          deliveryNotesEnabled
          deliveryNotesLabel
          lowStockThreshold
          giftWrappingEnabled
          giftOrdersEnabled

          orderCancelEnabled
          orderCancelWindowHours
          orderCancelRequireReason

          enableB2bCheckout
        }
      }
      ... on LanguageNotAvailableError {
        errorCode
        message
      }
    }
  }
`;

export const GET_GIFT_WRAP_OPTIONS = gql`
  query GetGiftWrapOptions {
    giftWrapOptions {
      id
      name
      price
      sortOrder
      isActive
      imageAssetId
      translations {
        id
        languageCode
        name
      }
    }
  }
`;

export const CREATE_GIFT_WRAP_OPTION = gql`
  mutation CreateGiftWrapOption($input: CreateGiftWrapOptionInput!) {
    createGiftWrapOption(input: $input) {
      id
      name
      price
      sortOrder
      isActive
      imageAssetId
      translations {
        id
        languageCode
        name
      }
    }
  }
`;

export const UPDATE_GIFT_WRAP_OPTION = gql`
  mutation UpdateGiftWrapOption($id: ID!, $input: UpdateGiftWrapOptionInput!) {
    updateGiftWrapOption(id: $id, input: $input) {
      id
      name
      price
      sortOrder
      isActive
      imageAssetId
      translations {
        id
        languageCode
        name
      }
    }
  }
`;

export const DELETE_GIFT_WRAP_OPTION = gql`
  mutation DeleteGiftWrapOption($id: ID!) {
    deleteGiftWrapOption(id: $id)
  }
`;

export const GET_WEBHOOK_ENDPOINTS = gql`
  query GetWebhookEndpoints {
    webhookEndpoints {
      id
      createdAt
      updatedAt
      url
      description
      events
      enabled
      failureCount
    }
  }
`;

export const GET_DEVELOPER_INTEGRATION_CAPABILITIES = gql`
  query GetDeveloperIntegrationCapabilities {
    developerIntegrationCapabilities {
      merchantDeveloperIntegrations
    }
  }
`;

export const GET_WEBHOOK_DELIVERIES = gql`
  query GetWebhookDeliveries($endpointId: ID!, $options: WebhookDeliveryListOptions) {
    webhookDeliveries(endpointId: $endpointId, options: $options) {
      items {
        id
        createdAt
        eventType
        status
        httpStatus
        attempts
        duration
        error
        responseBody
        payload
      }
      totalItems
    }
  }
`;

export const GET_AVAILABLE_WEBHOOK_EVENTS = gql`
  query GetAvailableWebhookEvents {
    availableWebhookEvents {
      eventType
      description
      category
    }
  }
`;

export const CREATE_WEBHOOK_ENDPOINT = gql`
  mutation CreateWebhookEndpoint($input: CreateWebhookEndpointInput!) {
    createWebhookEndpoint(input: $input) {
      endpoint {
        id
        url
        description
        events
        enabled
      }
      secret
    }
  }
`;

export const UPDATE_WEBHOOK_ENDPOINT = gql`
  mutation UpdateWebhookEndpoint($input: UpdateWebhookEndpointInput!) {
    updateWebhookEndpoint(input: $input) {
      id
      url
      description
      events
      enabled
    }
  }
`;

export const DELETE_WEBHOOK_ENDPOINT = gql`
  mutation DeleteWebhookEndpoint($id: ID!) {
    deleteWebhookEndpoint(id: $id) {
      result
      message
    }
  }
`;

export const REGENERATE_WEBHOOK_SECRET = gql`
  mutation RegenerateWebhookSecret($id: ID!) {
    regenerateWebhookSecret(id: $id) {
      secret
    }
  }
`;

export const TEST_WEBHOOK_ENDPOINT = gql`
  mutation TestWebhookEndpoint($id: ID!) {
    testWebhookEndpoint(id: $id) {
      success
      deliveryId
    }
  }
`;

export const RETRY_WEBHOOK_DELIVERY = gql`
  mutation RetryWebhookDelivery($id: ID!) {
    retryWebhookDelivery(id: $id) {
      id
      status
      attempts
    }
  }
`;

export const GET_REST_API_KEYS = gql`
  query GetRestApiKeys {
    restApiKeys {
      id
      createdAt
      name
      keyPrefix
      permissions
      lastUsedAt
      expiresAt
      enabled
    }
  }
`;

export const CREATE_REST_API_KEY = gql`
  mutation CreateRestApiKey($input: CreateRestApiKeyInput!) {
    createRestApiKey(input: $input) {
      apiKey {
        id
        name
        keyPrefix
        permissions
        expiresAt
        enabled
      }
      rawKey
    }
  }
`;

export const REVOKE_REST_API_KEY = gql`
  mutation RevokeRestApiKey($id: ID!) {
    revokeRestApiKey(id: $id) {
      result
      message
    }
  }
`;

export const GET_SEO_SETTINGS = gql`
  query GetSeoSettings {
    activeChannel {
      id
      customFields {
        seoTitleTemplate
        seoDescription
        robotsTxt
        sitemapEnabled
        googleVerificationCode
        ogImageAsset {
          id
          preview
          name
        }
      }
    }
  }
`;

export const UPDATE_SEO_SETTINGS = gql`
  mutation UpdateSeoSettings($input: UpdateChannelInput!) {
    updateChannel(input: $input) {
      ... on Channel {
        id
        customFields {
          seoTitleTemplate
          seoDescription
          robotsTxt
          sitemapEnabled
          googleVerificationCode
          ogImageAsset {
            id
            preview
            name
          }
        }
      }
      ... on LanguageNotAvailableError {
        errorCode
        message
      }
    }
  }
`;

export const GET_URL_REDIRECTS = gql`
  query GetUrlRedirects($options: UrlRedirectListOptions) {
    urlRedirects(options: $options) {
      items {
        id
        createdAt
        fromPath
        toPath
        type
        isActive
        hitCount
      }
      totalItems
    }
  }
`;

export const CREATE_URL_REDIRECT = gql`
  mutation CreateUrlRedirect($input: CreateUrlRedirectInput!) {
    createUrlRedirect(input: $input) {
      id
      fromPath
      toPath
      type
      isActive
    }
  }
`;

export const UPDATE_URL_REDIRECT = gql`
  mutation UpdateUrlRedirect($input: UpdateUrlRedirectInput!) {
    updateUrlRedirect(input: $input) {
      id
      fromPath
      toPath
      type
      isActive
    }
  }
`;

export const DELETE_URL_REDIRECT = gql`
  mutation DeleteUrlRedirect($id: ID!) {
    deleteUrlRedirect(id: $id) {
      result
      message
    }
  }
`;

export const BULK_IMPORT_URL_REDIRECTS = gql`
  mutation BulkImportUrlRedirects($input: [BulkImportUrlRedirectInput!]!) {
    bulkImportUrlRedirects(input: $input) {
      imported
      skipped
      errors
    }
  }
`;

export const GET_ACTIVE_CHANNEL_CURRENCY = gql`
  query GetActiveChannelCurrency {
    activeChannel {
      id
      currencyCode
      defaultCurrencyCode
      defaultLanguageCode
      availableCurrencyCodes
      pricesIncludeTax
    }
  }
`;

export const GET_POST_ORDER_SETTINGS_LOCK = gql`
  query GetPostOrderSettingsLock {
    postOrderSettingsLock {
      currencyLocked
      timezoneLocked
      lockedAt
      reasonCode
    }
  }
`;

export const GET_MARKETS = gql`
  query GetMarkets {
    markets {
      items {
        market {
          id
          name
          countryCodes
          currencyCode
          languageCode
          isActive
          activationState
          activationError
          createdAt
          updatedAt
        }
        exchangeRate
        lastUpdated
      }
      totalItems
    }
  }
`;

export const GET_MARKET_READINESS = gql`
  query GetMarketReadiness {
    marketReadiness {
      countryCode
      countryNameEn
      countryNameAr
      currencyCode
      marketId
      isActive
      activationState
      shippingReady
      status
      blockerReasons
    }
  }
`;

export const CREATE_MARKET = gql`
  mutation CreateMarket($input: CreateMarketInput!) {
    createMarket(input: $input) {
      id
      name
      countryCodes
      currencyCode
      languageCode
      isActive
      activationState
    }
  }
`;

export const TOGGLE_MARKET_ACTIVE = gql`
  mutation ToggleMarketActive($id: ID!, $isActive: Boolean!) {
    toggleMarketActive(id: $id, isActive: $isActive) {
      id
      isActive
      activationState
    }
  }
`;

export const GET_LANGUAGE_SETTINGS = gql`
  query GetLanguageSettings {
    activeChannel {
      id
      defaultLanguageCode
      availableLanguageCodes
    }
  }
`;

export const UPDATE_LANGUAGE_SETTINGS = gql`
  mutation UpdateLanguageSettings($input: UpdateChannelInput!) {
    updateChannel(input: $input) {
      ... on Channel {
        id
        defaultLanguageCode
        availableLanguageCodes
      }
      ... on LanguageNotAvailableError {
        errorCode
        message
      }
    }
  }
`;

// ============================================================
// Testimonials (Sprint TRUST)
// ============================================================

export const GET_MEILISEARCH_STATUS = gql`
  query MeilisearchStatus {
    meilisearchStatus {
      healthy
      indexUid
      documentCount
      collectionsIndex {
        indexUid
        documentCount
      }
      pagesIndex {
        indexUid
        documentCount
      }
    }
  }
`;

export const MEILISEARCH_REINDEX = gql`
  mutation MeilisearchReindex {
    meilisearchReindex {
      success
      indexed
    }
  }
`;
